import { useCallback, useEffect, useRef, useState } from 'react'
import { useDealLoadStatus } from './use-deal-load-status'

const DEFAULT_POLL_INTERVAL_MS = 3000
const DEFAULT_TARGET_STATUS = 'Completed'
/** ~1 minute at default 3-second intervals before surfacing a timeout error. */
const DEFAULT_MAX_RETRIES = 20

interface UseDealLoadStatusPollingOptions {
  /** Polling interval in milliseconds. Defaults to 3000. */
  intervalMs?: number
  /** Target dealStatus string that stops polling and triggers onStatusCompleted. Defaults to 'Completed'. */
  targetStatus?: string
  /**
   * When true, skips mutating shared reactive vars (dealLoadStatusVar,
   * shouldDisableActionBtns). Pass true when polling from pages other than
   * the decision page to avoid cross-page state side effects.
   */
  skipSideEffects?: boolean
  /**
   * Maximum number of poll attempts before stopping and setting `pollError`.
   * Defaults to 20 (~1 minute at 3-second intervals).
   */
  maxRetries?: number
  /** Called once when the polled dealStatus matches targetStatus. */
  onStatusCompleted: () => void
}

/**
 * Polls GetDealLoadStatus at a fixed interval until `dealStatus` matches
 * `targetStatus`, then stops and invokes `onStatusCompleted`.
 *
 * Polling starts automatically when both `dealId` and `userId` are defined,
 * and stops on component unmount.
 *
 * @returns `isPolling` — true while polling is active (i.e. target not yet reached)
 */
export const useDealLoadStatusPolling = (
  dealId: number | undefined,
  userId: string | undefined,
  options: UseDealLoadStatusPollingOptions
) => {
  const {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    targetStatus = DEFAULT_TARGET_STATUS,
    skipSideEffects = false,
    maxRetries = DEFAULT_MAX_RETRIES,
    onStatusCompleted,
  } = options

  const { fetchDealLoadStatus } = useDealLoadStatus({ skipSideEffects })

  const [isPolling, setIsPolling] = useState(false)
  const [pollError, setPollError] = useState(false)
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isCompletedRef = useRef(false)
  const retriesRef = useRef(0)
  const onStatusCompletedRef = useRef<() => void>(onStatusCompleted)

  // Keep callback ref up-to-date without restarting the effect
  useEffect(() => {
    onStatusCompletedRef.current = onStatusCompleted
  }, [onStatusCompleted])

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    setIsPolling(false)
  }, [])

  useEffect(() => {
    if (!dealId || !userId) return

    isCompletedRef.current = false
    retriesRef.current = 0
    setPollError(false)
    setIsPolling(true)

    const poll = async () => {
      if (isCompletedRef.current) return

      retriesRef.current += 1
      if (retriesRef.current > maxRetries) {
        stopPolling()
        setPollError(true)
        return
      }

      try {
        const result = await fetchDealLoadStatus(dealId, userId)
        const dealStatus = result?.data?.dealLoadStatus?.dealStatus

        if (dealStatus === targetStatus && !isCompletedRef.current) {
          isCompletedRef.current = true
          stopPolling()
          onStatusCompletedRef.current()
        }
      } catch (err) {
        console.error('Error polling deal load status:', err)
      }
    }

    poll()
    pollingIntervalRef.current = setInterval(poll, intervalMs)

    return () => {
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, userId])

  return { isPolling, pollError }
}
